import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import WelcomeExploreUnknownLeaderboard from './WelcomeExploreUnknownLeaderboard';
import WelcomeHonorHallMovements from './WelcomeHonorHallMovements';
import WelcomeLatestRemnants from './WelcomeLatestRemnants';
import WelcomeSeasonPanel from './WelcomeSeasonPanel';

/** Industry-standard carousel dwell (Bootstrap / most marketing carousels). */
const AUTO_ADVANCE_MS = 5000;

const SLIDE_COUNT = 4;

/**
 * Cycles welcome info panels (Explore Unknown, Honor Hall, Remnants, Season).
 * @param {{ isMobile?: boolean, isDevPlayer1?: boolean }} props
 */
function WelcomeLeftStackCarousel({ isMobile = false, isDevPlayer1 = false }) {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const goTo = useCallback((index) => {
    if (index < 0 || index >= SLIDE_COUNT) {
      throw new Error(`WelcomeLeftStackCarousel: slide index out of range (${index})`);
    }
    setActiveIndex(index);
  }, []);

  const goPrev = useCallback(() => {
    setActiveIndex((current) => (current - 1 + SLIDE_COUNT) % SLIDE_COUNT);
  }, []);

  const goNext = useCallback(() => {
    setActiveIndex((current) => (current + 1) % SLIDE_COUNT);
  }, []);

  const handleViewportWheel = useCallback((event) => {
    const scrollable = event.target.closest(
      '.welcome-eu-leaderboard-list--scroll, .welcome-season-panel-body'
    );
    if (!scrollable) {
      return;
    }
    if (scrollable.scrollHeight > scrollable.clientHeight + 1) {
      event.stopPropagation();
    }
  }, []);

  useEffect(() => {
    if (paused) {
      return undefined;
    }
    const id = setInterval(() => {
      setActiveIndex((current) => (current + 1) % SLIDE_COUNT);
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(id);
  }, [paused, activeIndex]);

  const slideLabels = [
    t('welcome.exploreUnknownLeaderboard.ariaLabel'),
    t('welcome.honorHallMovements.ariaLabel'),
    t('welcome.latestRemnants.ariaLabel'),
    t('welcome.seasonPanel.ariaLabel'),
  ];

  return (
    <div
      className={`welcome-left-carousel${isMobile ? ' welcome-left-carousel--mobile' : ''}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setPaused(false);
        }
      }}
    >
      <div className="welcome-left-carousel__stage">
        <button
          type="button"
          className="welcome-left-carousel__arrow welcome-left-carousel__arrow--prev"
          aria-label={t('welcome.leftStackCarousel.previous')}
          onClick={goPrev}
        >
          <span aria-hidden="true">‹</span>
        </button>

        <div
          className="welcome-left-carousel__viewport"
          aria-live="polite"
          aria-roledescription="carousel"
          aria-label={t('welcome.leftStackCarousel.ariaLabel')}
          onWheel={handleViewportWheel}
        >
          {/* Shared painted frame — stays mounted so slide fades only swap content */}
          <div className="welcome-left-carousel__panel-bg" aria-hidden="true" />
          <div
            className={`welcome-left-carousel__slide${activeIndex === 0 ? ' is-active' : ''}`}
            aria-hidden={activeIndex !== 0}
          >
            <WelcomeExploreUnknownLeaderboard isMobile={isMobile} />
          </div>
          <div
            className={`welcome-left-carousel__slide${activeIndex === 1 ? ' is-active' : ''}`}
            aria-hidden={activeIndex !== 1}
          >
            <WelcomeHonorHallMovements isMobile={isMobile} isDevPlayer1={isDevPlayer1} />
          </div>
          <div
            className={`welcome-left-carousel__slide${activeIndex === 2 ? ' is-active' : ''}`}
            aria-hidden={activeIndex !== 2}
          >
            <WelcomeLatestRemnants isMobile={isMobile} />
          </div>
          <div
            className={`welcome-left-carousel__slide${activeIndex === 3 ? ' is-active' : ''}`}
            aria-hidden={activeIndex !== 3}
          >
            <WelcomeSeasonPanel isMobile={isMobile} isDevPlayer1={isDevPlayer1} />
          </div>
        </div>

        <button
          type="button"
          className="welcome-left-carousel__arrow welcome-left-carousel__arrow--next"
          aria-label={t('welcome.leftStackCarousel.next')}
          onClick={goNext}
        >
          <span aria-hidden="true">›</span>
        </button>
      </div>

      <div
        className="welcome-left-carousel__dots"
        role="tablist"
        aria-label={t('welcome.leftStackCarousel.dotsAriaLabel')}
      >
        {slideLabels.map((label, index) => (
          <button
            key={index}
            type="button"
            role="tab"
            className={`welcome-left-carousel__dot${activeIndex === index ? ' is-active' : ''}`}
            aria-selected={activeIndex === index}
            aria-label={t('welcome.leftStackCarousel.showSlide', { label })}
            onClick={() => goTo(index)}
          />
        ))}
      </div>
    </div>
  );
}

export default WelcomeLeftStackCarousel;
